package com.eladfinish.bible.contest.screens.browse.usecases

import com.eladfinish.bible.contest.screens.lexicographicalSort.usecases.GeneratePdfFromHtmlApiUseCase
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

/**
 * There's no local HTML-to-PDF API a browser sandbox can call (Android/iOS both render via a
 * hidden WebView's print-to-PDF; browsers only expose an interactive `window.print()` dialog,
 * not a way to get PDF bytes back from script). So this sends the HTML to the same Gotenberg
 * conversion the "email PDF" flow already uses server-side and gets bytes back over HTTP instead.
 *
 * [htmlContent] arrives already run through `PrepareHtmlForPdfUseCase` (the common
 * `LexicographicalSortPdfViewerStore.generatePdf()` call site does that unconditionally, since
 * it's also needed by Android/iOS's local WebView rendering) — that inserts a client-side
 * `<style>` block and a footer `<div>` that would double up with the server's own equivalents,
 * so both are stripped back out before sending.
 */
class GenerateHtmlToPdfUseCaseImpl(
    private val generatePdfFromHtmlApiUseCase: GeneratePdfFromHtmlApiUseCase,
) : GenerateHtmlToPdfUseCase {

    override fun invoke(
        htmlContent: String,
        onGenerated: (pdfBytes: ByteArray) -> Unit,
        onError: (Throwable) -> Unit,
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            generatePdfFromHtmlApiUseCase(tableHtml = stripClientPdfPrep(htmlContent), fileName = "table.pdf")
                .onSuccess { pdfBytes -> onGenerated(pdfBytes) }
                .onFailure { throwable -> onError(throwable) }
        }
    }

    private fun stripClientPdfPrep(html: String): String {
        val withoutStyle = html.replace(Regex("""<style>[\s\S]*?</style>\s*(?=</head>)"""), "")
        return withoutStyle.replace(Regex("""<div class="pdf-footer-credit">[\s\S]*?</div>\s*(?=</body>)"""), "")
    }
}

class OpenPdfUseCaseImpl : OpenPdfUseCase {
    override val canOpenPdf: Boolean = true

    // [uri] is the object URL SavePdfBytesUseCaseImpl created for the same bytes it already
    // triggered a download of — opening it just re-shows that PDF in a new tab.
    override fun invoke(uri: String) {
        window.open(uri, "_blank")
    }
}

class CountPdfPagesUseCaseImpl : CountPdfPagesUseCase {
    // Counts the page objects ("/Type /Page", as opposed to the "/Type /Pages" tree nodes) in the
    // file's plain-text object dictionaries. That is how the PDFs this app handles are written
    // (Chromium's PDF backend, via Gotenberg); a PDF that packs its objects into compressed
    // object streams would count as 0.
    override fun invoke(pdfBytes: ByteArray): Int {
        val text = CharArray(pdfBytes.size) { (pdfBytes[it].toInt() and 0xFF).toChar() }.concatToString()
        return pageObject.findAll(text).count()
    }

    private companion object {
        val pageObject = Regex("""/Type\s*/Page(?![A-Za-z])""")
    }
}
